import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef
} from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import {
  CalendarEventResponseStatusEnum, EntitiesReportInput, ExportEntitiesGQL
} from 'generated/graphql';

import { makeResponsesInput } from 'app/events/helpers/export-responses';
import { tagFilterFunction } from 'app/events/helpers/tag-filter-function';
import { ToastrService } from 'core/toastr.service';

interface ExportEventsDialogData {
  calendarEventIds: string[];
  startDate?: Date;
  endDate?: Date;
  searchTerm?: string;
}

@Component({
  selector: 'app-export-events-dialog',
  templateUrl: `./export-events.dialog.html`,
  styleUrls: ['./export-events.dialog.scss'],
})
export class ExportEventsDialog implements OnInit {
  static open(dialog: MatDialog, config: MatDialogConfig<ExportEventsDialogData>) {
    return dialog.open(ExportEventsDialog, config);
  }

  exportTypeUi: 'responses' | 'events' = 'responses';
  availableTagCategories: Array<{ id: string }> = [];
  selectedCategories: Array<{ id: string }> = [];
  canExportTags = true;
  rsvpStatuses: CalendarEventResponseStatusEnum[] = Object.values(CalendarEventResponseStatusEnum);
  searchTermDisplay = '';

  constructor(
    private toastrService: ToastrService,
    private _exportEntitiesGQL: ExportEntitiesGQL,
    public dialogRef: MatDialogRef<ExportEventsDialog>,
    private route: ActivatedRoute,
    @Inject(MAT_DIALOG_DATA) public data: ExportEventsDialogData
  ) {
  }

  get campaignId() {
    return this.route.snapshot.queryParamMap.get('campaignId');
  }

  close(wasSuccess: boolean) {
    this.dialogRef.close(wasSuccess);
  }

  ngOnInit() {
    if (this.data.searchTerm && this.data.searchTerm.trim().length > 0) {
      this.searchTermDisplay = `"${this.data.searchTerm}"`;
    } else {
      this.searchTermDisplay = '';
    }
  }

  export() {
    if (this.exportTypeUi === 'events') {
      this.exportEvents();
    }
    if (this.exportTypeUi === 'responses') {
      this.exportResponses();
    }
  }

  exportEvents() {
    const input: EntitiesReportInput = this._makeCalendarCountsInput();
    this._executeMutation(input).subscribe({
      next: (response) => {
        const wasSuccess = response.data?.entitiesReport.success;
        this.toastrService.success('A link to download the file will be emailed to you');
        this.close(wasSuccess);
      },
      error: (error) => {
        this.toastrService.error('There was a problem exporting the file, please try again later.');
      }
    });
  }

  exportResponses() {
    const input: EntitiesReportInput = makeResponsesInput({
      campaignId: this.campaignId,
      calendarEventIds: this.data.calendarEventIds,
      selectedCategories: this.selectedCategories,
      rsvpStatuses: this.rsvpStatuses,
    });
    this._executeMutation(input).subscribe({
      next: (response) => {
        const wasSuccess = response.data?.entitiesReport.success;
        this.toastrService.success('A link to download the file will be emailed to you');
        this.close(wasSuccess);
      },
      error: (error) => {
        console.log(error);
        this.toastrService.error('There was a problem exporting the file, please try again later.');
      }
    });
  }

  buildCategoryFilterFunction() {
    return tagFilterFunction({
      availableTagCategories: this.availableTagCategories,
      canExportTags: this.canExportTags,
    });
  }

  selectedCategoriesChanged(event: { id: string }[]) {
    this.selectedCategories = event || [];
  }

  private _makeCalendarCountsInput(): EntitiesReportInput {
    return {
      params: {
        reportType: 'calendar_event_counts',
        campaignId: this.campaignId,
        extraParams: {
          calendar_event_ids: this.data.calendarEventIds,
        }
      }
    };
  }

  private _executeMutation(input: EntitiesReportInput) {
    return this._exportEntitiesGQL.mutate({ input });
  }
}
